import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { confirmPasswordReset } from '../../api/auth.api';
import { PasswordResetForm, TokenValidationLoading, TokenValidationError } from '../../components/auth';
import LOGO from '../../assets/LOGO (2).png';
import featureIcon1 from '../../assets/material-symbols_space-dashboard.png';
import featureIcon2 from '../../assets/streamline_money-graph-analytics-business-product-graph-data-chart-analysis.png';
import featureIcon3 from '../../assets/ix_user-management-settings-filled.png';
import featureIcon4 from '../../assets/fluent-mdl2_b-i-dashboard.png';
import ellipse1 from '../../assets/Ellipse 1.png';
import ellipse2 from '../../assets/Ellipse 2.png';
import ellipse3 from '../../assets/Ellipse 3.png';
import ellipse4 from '../../assets/Ellipse 4.png';

const RightPanel = () => (
  <div className="relative hidden h-full w-full items-center justify-center overflow-hidden bg-teal-700 px-10 py-16 text-white lg:flex">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.12),transparent_45%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.08),transparent_50%)]" />
    </div>

    <div className="relative z-10 flex h-full w-full max-w-xl items-start justify-center px-6 pt-0 mt-[-3rem]">
      <div className="relative h-[29rem] w-[29rem]">
        <div className="absolute left-[14rem] top-0 flex flex-col items-center text-center">
          <div className="relative flex h-28 w-28 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse1} alt="" aria-hidden className="h-full w-full object-contain" />
            <img src={featureIcon1} alt="Espace sécurisé" className="absolute h-12 w-12" />
          </div>
          <p className="mt-3 text-base font-medium text-white">Espace sécurisé</p>
        </div>

        <div className="absolute left-[0rem] top-[9rem] flex flex-col items-center text-center">
          <div className="relative flex h-28 w-28 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse3} alt="" aria-hidden className="h-full w-full object-contain" />
            <img src={featureIcon3} alt="Gestion simplifiée" className="absolute h-12 w-12" />
          </div>
          <p className="mt-3 text-base font-medium text-white">Gestion simplifiée</p>
        </div>

        <div className="absolute left-[18rem] top-[11rem] flex flex-col items-center text-center">
          <div className="relative flex h-28 w-28 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse2} alt="" aria-hidden className="h-full w-full object-contain" />
            <img
              src={featureIcon2}
              alt="Suivi en temps réel"
              className="absolute h-12 w-12 filter brightness-0 invert"
            />
          </div>
          <p className="mt-3 text-base font-medium text-white">Suivi en temps réel</p>
        </div>

        <div className="absolute left-[-1rem] top-[20rem] flex flex-col items-center text-center">
          <div className="relative flex h-28 w-28 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse4} alt="" aria-hidden className="h-full w-full object-contain" />
            <img src={featureIcon4} alt="Support continu" className="absolute h-12 w-12" />
          </div>
          <p className="mt-3 text-base font-medium text-white leading-snug text-center">
            Un accompagnement constant pour vos équipes.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const ResetPasswordConfirm = () => {
  const [isValidToken, setIsValidToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { uid, token } = useParams();

  useEffect(() => {
    if (!uid || !token) {
      setIsValidToken(false);
      toast.error('Lien de réinitialisation invalide.');
    } else {
      setIsValidToken(true);
    }
  }, [uid, token]);

  const handleSubmit = async ({ newPassword, confirmPassword }) => {
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas.');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset(uid, token, newPassword);
      toast.success('Mot de passe réinitialisé avec succès !');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          toast.error(data?.new_password?.[0] || data?.token?.[0] || data?.uid?.[0] || 'Données invalides.');
        } else if (status === 404) {
          toast.error('Utilisateur non trouvé.');
        } else {
          toast.error(data?.detail || `Erreur (${status}). Veuillez réessayer.`);
        }
      } else if (error.request) {
        toast.error('Le serveur ne répond pas. Vérifie ta connexion réseau.');
      } else {
        toast.error('Erreur de configuration de la requête.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidToken === null) {
    return <TokenValidationLoading />;
  }

  if (isValidToken === false) {
    return <TokenValidationError onRetry={() => navigate('/mot-de-passe-oublie')} />;
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_1fr]">
      {/* Left panel */}
      <div className="flex flex-col items-center bg-white bg-none relative px-6 py-10 sm:px-12 md:px-16 lg:justify-start lg:pt-28">
        <div className="relative z-10 flex flex-col items-center gap-8">
          <div className="text-center">
            <img src={LOGO} alt="logo-easy-survey" className="mx-auto mb-6 h-28 w-auto" />
            <p className="mt-2 w-full text-center text-base font-bold text-black md:text-lg">
              Réinitialisez votre mot de passe en toute sécurité.
            </p>
          </div>

          <PasswordResetForm onSubmit={handleSubmit} isLoading={isLoading} />
        </div>
      </div>

      {/* Right panel */}
      <RightPanel />
    </div>
  );
};

export default ResetPasswordConfirm;
